import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ViewMode, getISOWeek } from '../hooks/usePlantafel';
import { CrmTask } from '../../sales/services/CrmTaskService';
import { AssignedEmployee, Shift } from '../../shifts/services/ShiftService';
import { LaneTeam, LaneGroup, ProjectTimelineItem, EmployeeJobConflict } from '../services/PlantafelService';
import PlantafelService from '../services/PlantafelService';
import absenceService, { Absence } from '../../absences/services/AbsenceService';
import { formatLocalDate } from '../utils/plantafelTime';
import { pdebug, pdebugError } from '../utils/plantafelDebug';

interface UsePlantafelHandlersDeps {
  loading: boolean;
  error: string | null;
  laneGroups: LaneGroup[];
  shifts: Shift[];
  backlogTasks: CrmTask[];
  weekStart: Date;
  days: any[];
  settings: any;
  selectedShift: any;
  refresh: () => Promise<void>;
  handleShiftClick: (shift: Shift, task: CrmTask | null) => void;
  scheduleBacklogTask: (taskId: number, teamId: number, dateStr: string, taskTitle: string, teamName: string) => Promise<void>;
  updateShiftOptimistically: (shiftId: number, updater: (shift: any) => any) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  anchorDate: Date;
  setAnchorDate: (date: Date) => void;
}

export function usePlantafelHandlers(deps: UsePlantafelHandlersDeps) {
  const navigate = useNavigate();
  const {
    laneGroups, shifts, backlogTasks, weekStart, days, refresh,
    handleShiftClick, scheduleBacklogTask, updateShiftOptimistically,
    viewMode, setViewMode, anchorDate, setAnchorDate,
  } = deps;

  const handleOpenProject = useCallback((projectId: number) => {
    navigate(`/projects/${projectId}`);
  }, [navigate]);

  const [projectsTimeline, setProjectsTimeline] = useState<ProjectTimelineItem[]>([]);
  const [allProjects, setAllProjects] = useState<ProjectTimelineItem[]>([]);
  const [showProjectsTimeline, setShowProjectsTimeline] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [absModal, setAbsModal] = useState<{
    open: boolean; date?: string; type?: 'approved' | 'requested'; anchor?: { top: number; left: number };
  }>({ open: false });
  const [activeEmployeeId, setActiveEmployeeId] = useState<number | null>(null);
  const [highlightedShiftId, setHighlightedShiftId] = useState<number | null>(null);
  const [teamDropTargetId, setTeamDropTargetId] = useState<number | null>(null);
  const [employeeConflictModal, setEmployeeConflictModal] = useState<{
    employeeId: number; employeeName: string; targetShiftId: number; conflict: EmployeeJobConflict;
  } | null>(null);

  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeEmployeeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
      if (activeEmployeeTimeoutRef.current) clearTimeout(activeEmployeeTimeoutRef.current);
    };
  }, []);

  const handleHighlightShift = useCallback((shiftId: number) => {
    setHighlightedShiftId(shiftId);
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => setHighlightedShiftId(null), 1500);
  }, []);

  const handleAbsentEmployeeClick = useCallback((employeeId: number) => {
    setActiveEmployeeId(employeeId);
    if (activeEmployeeTimeoutRef.current) clearTimeout(activeEmployeeTimeoutRef.current);
    activeEmployeeTimeoutRef.current = setTimeout(() => setActiveEmployeeId(null), 3500);
  }, []);

  const filteredShifts = useMemo(() => {
    pdebug('PlantafelBoard: shifts from hook:', shifts.length, 'selectedProjectId:', selectedProjectId);
    let result = shifts;
    if (selectedProjectId !== null) {
      result = result.filter(s => (s as any).project_id === selectedProjectId);
    }
    pdebug('PlantafelBoard: filteredShifts:', result.length);
    return result;
  }, [shifts, selectedProjectId]);

  const filteredBacklogTasks = useMemo(() => {
    if (selectedProjectId === null) return backlogTasks;
    return backlogTasks.filter(t => t.project === selectedProjectId);
  }, [backlogTasks, selectedProjectId]);

  const workAreaColorMap = useMemo(() => {
    const map = new Map<number, string>();
    laneGroups.forEach(group => {
      map.set(group.workAreaId, group.workAreaColor || '#7c3aed');
      group.teams.forEach(team => {
        map.set(team.id, team.work_area_color || group.workAreaColor || '#7c3aed');
      });
    });
    return map;
  }, [laneGroups]);

  const assignedAssetMap = useMemo(() => {
    const map = new Map<number, { shiftId: number; jobTitle: string; workAreaColor?: string }>();
    shifts.forEach(shift => {
      if (shift.asset_assignments) {
        const waId = typeof shift.work_area === 'number' ? shift.work_area : parseInt(String(shift.work_area), 10);
        const teamId = shift.work_area_team_id;
        const color = (teamId && workAreaColorMap.get(teamId)) || workAreaColorMap.get(waId) || shift.work_area__color;
        shift.asset_assignments.forEach(assignment => {
          map.set(assignment.asset_id, { shiftId: shift.id, jobTitle: shift.crm_task_title || 'Job', workAreaColor: color });
        });
      }
    });
    return map;
  }, [shifts, workAreaColorMap]);

  const assignedEmployeeMap = useMemo(() => {
    const map = new Map<number, { shiftId: number; jobTitle: string; workAreaColor?: string }>();
    shifts.forEach(shift => {
      if (shift.assigned_employees) {
        const waId = typeof shift.work_area === 'number' ? shift.work_area : parseInt(String(shift.work_area), 10);
        const teamId = shift.work_area_team_id;
        const color = (teamId && workAreaColorMap.get(teamId)) || workAreaColorMap.get(waId) || shift.work_area__color;
        shift.assigned_employees.forEach((emp: AssignedEmployee) => {
          const empId = emp.id || emp.employee_id;
          if (empId) {
            map.set(empId, { shiftId: shift.id, jobTitle: shift.crm_task_title || 'Job', workAreaColor: color });
          }
        });
      }
    });
    return map;
  }, [shifts, workAreaColorMap]);

  const employeeHoursMap = useMemo(() => {
    const MAX_HOURS_PER_DAY = 8;
    const map = new Map<number, number>();
    const calculateShiftHours = (startTime: string, endTime: string): number => {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      if (startDay.getTime() === endDay.getTime()) {
        return Math.min((end.getTime() - start.getTime()) / (1000 * 60 * 60), MAX_HOURS_PER_DAY);
      }
      let totalHours = 0;
      let currentDay = new Date(startDay);
      while (currentDay <= endDay) {
        const dayStart = currentDay.getTime() === startDay.getTime()
          ? start : new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate(), 0, 0, 0);
        const dayEnd = currentDay.getTime() === endDay.getTime()
          ? end : new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate(), 23, 59, 59);
        totalHours += Math.min((dayEnd.getTime() - dayStart.getTime()) / (1000 * 60 * 60), MAX_HOURS_PER_DAY);
        currentDay.setDate(currentDay.getDate() + 1);
      }
      return totalHours;
    };
    shifts.forEach(shift => {
      if (shift.assigned_employees && shift.assigned_employees.length > 0) {
        const hours = calculateShiftHours(shift.start_time, shift.end_time);
        shift.assigned_employees.forEach((emp: AssignedEmployee) => {
          const empId = emp.id || emp.employee_id || emp.employee;
          if (empId) { map.set(empId, (map.get(empId) || 0) + hours); }
        });
      }
    });
    return map;
  }, [shifts]);

  const weekStartStr = useMemo(() => formatLocalDate(weekStart), [weekStart]);

  const { waSimple, waComplex } = useMemo(() => {
    const simple: LaneGroup[] = [];
    const complex: LaneGroup[] = [];
    laneGroups.forEach((group) => {
      if (group.teams.length <= 1) simple.push(group);
      else complex.push(group);
    });
    return { waSimple: simple, waComplex: complex };
  }, [laneGroups]);

  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [rosterModalOpen, setRosterModalOpen] = useState(false);
  const [selectedWorkArea, setSelectedWorkArea] = useState<LaneGroup | null>(null);
  const [editingTeam, setEditingTeam] = useState<LaneTeam | null>(null);
  const [selectedTeamForRoster, setSelectedTeamForRoster] = useState<LaneTeam | null>(null);
  const [createJobModal, setCreateJobModal] = useState<{ teamId: number; dateStr: string } | null>(null);
  const [scheduleModal, setScheduleModal] = useState<{ isOpen: boolean; task: CrmTask | null }>({ isOpen: false, task: null });

  const handleScheduleClick = useCallback((task: CrmTask) => { setScheduleModal({ isOpen: true, task }); }, []);
  const closeScheduleModal = useCallback(() => { setScheduleModal({ isOpen: false, task: null }); }, []);

  const handleAddTeam = useCallback((group: LaneGroup) => { setSelectedWorkArea(group); setEditingTeam(null); setTeamModalOpen(true); }, []);
  const handleTeamModalClose = useCallback(() => { setTeamModalOpen(false); setSelectedWorkArea(null); setEditingTeam(null); }, []);
  const handleTeamSaved = useCallback(async () => { handleTeamModalClose(); await refresh(); }, [handleTeamModalClose, refresh]);
  const handleEditRoster = useCallback((team: LaneTeam) => { setSelectedTeamForRoster(team); setRosterModalOpen(true); }, []);
  const handleRosterModalClose = useCallback(() => { setRosterModalOpen(false); setSelectedTeamForRoster(null); }, []);
  const handleRosterSaved = useCallback(async () => { handleRosterModalClose(); await refresh(); }, [handleRosterModalClose, refresh]);
  const handleCreateJobClick = useCallback((teamId: number, dateStr: string) => { setCreateJobModal({ teamId, dateStr }); }, []);
  const handleCreateJobModalClose = useCallback(() => { setCreateJobModal(null); }, []);
  const handleJobCreated = useCallback(async (createdShift: import('../../shifts/services/ShiftService').Shift) => {
    setCreateJobModal(null); await refresh(); handleShiftClick(createdShift, null);
  }, [refresh, handleShiftClick]);
  const handleCreateProject = useCallback(() => { setCreateProjectModalOpen(true); }, []);

  const handleDayClickFromCalendar = useCallback((date: Date) => { setAnchorDate(date); setViewMode('week'); }, [setAnchorDate, setViewMode]);
  const handleMonthClickFromYear = useCallback((date: Date) => { setAnchorDate(date); setViewMode('month'); }, [setAnchorDate, setViewMode]);

  const handleUnscheduleShiftDrop = useCallback(async (shiftId: number) => {
    try { await PlantafelService.unscheduleShift(shiftId); await refresh(); }
    catch (err) { pdebugError('Failed to unschedule shift:', err); }
  }, [refresh]);

  const handleProjectCreated = useCallback(async (project: ProjectTimelineItem) => {
    setCreateProjectModalOpen(false);
    const lastDay = days[days.length - 1];
    const fromDate = formatLocalDate(weekStart);
    const toDate = lastDay ? lastDay.dateStr : formatLocalDate(weekStart);
    const [updatedTimeline, updatedAll] = await Promise.all([
      PlantafelService.fetchProjectsTimeline(fromDate, toDate), PlantafelService.fetchAllProjects(),
    ]);
    setProjectsTimeline(updatedTimeline); setAllProjects(updatedAll);
  }, [weekStart, days]);

  const handleDropProject = useCallback(async (projectId: number, dateStr: string, durationDays?: number) => {
    try {
      const duration = durationDays && durationDays > 0 ? durationDays : 14;
      const startDate = new Date(dateStr);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + duration - 1);
      const endDateStr = endDate.toISOString().split('T')[0];
      await PlantafelService.rescheduleProject(projectId, dateStr, endDateStr);
      toast.success('Projekt geplant');
      const lastDay = days[days.length - 1];
      const fromDate = formatLocalDate(weekStart);
      const toDate = lastDay ? lastDay.dateStr : formatLocalDate(weekStart);
      const [updatedTimeline, updatedAll] = await Promise.all([
        PlantafelService.fetchProjectsTimeline(fromDate, toDate), PlantafelService.fetchAllProjects(),
      ]);
      setProjectsTimeline(updatedTimeline); setAllProjects(updatedAll);
    } catch (err: any) {
      pdebugError('Failed to schedule project:', err); toast.error('Projekt konnte nicht geplant werden');
    }
  }, [weekStart, days]);

  const handleAssignEmployee = useCallback(async (shiftId: number, employeeId: number, employeeName?: string, fromShiftId?: number) => {
    try {
      if (fromShiftId && fromShiftId !== shiftId) {
        await PlantafelService.reassignEmployeeToShift(shiftId, employeeId, fromShiftId);
        toast.success(`${employeeName || 'Mitarbeiter'} umgeplant`);
        updateShiftOptimistically(fromShiftId, (shift) => ({
          ...shift, assigned_employees: (shift.assigned_employees || []).filter((e: AssignedEmployee) => e.id !== employeeId),
        }));
      } else {
        await PlantafelService.assignEmployeeToShift(shiftId, employeeId);
        toast.success('Mitarbeiter zugewiesen');
      }
      const [firstName, ...lastNameParts] = (employeeName || 'Mitarbeiter').split(' ');
      updateShiftOptimistically(shiftId, (shift) => ({
        ...shift, assigned_employees: [...(shift.assigned_employees || []), { id: employeeId, first_name: firstName, last_name: lastNameParts.join(' ') || '', employee_id: employeeId }],
      }));
      refresh().catch((err) => pdebugError('Background refresh failed after employee assign:', err));
    } catch (err: any) {
      pdebugError('Failed to assign employee:', err);
      if (err?.response?.status === 409) {
        const conflicts = err?.response?.data?.conflicts || [];
        if (conflicts.length > 0) {
          setEmployeeConflictModal({ employeeId, employeeName: employeeName || 'Mitarbeiter', targetShiftId: shiftId, conflict: conflicts[0] });
        } else {
          const detail = err?.response?.data?.detail || err?.response?.data?.message || '';
          toast.error(`Mitarbeiter-Konflikt: ${detail || 'Bereits einer anderen Schicht zugewiesen'}`);
        }
      } else { toast.error('Fehler beim Zuweisen des Mitarbeiters'); }
    }
  }, [refresh, updateShiftOptimistically]);

  const handleReplanEmployee = useCallback(async () => {
    if (!employeeConflictModal) return;
    const { employeeId, employeeName, targetShiftId, conflict } = employeeConflictModal;
    try {
      await PlantafelService.reassignEmployeeToShift(targetShiftId, employeeId, conflict.shift_id);
      toast.success(`${employeeName} umgeplant`);
      setEmployeeConflictModal(null);
      updateShiftOptimistically(conflict.shift_id, (shift) => ({
        ...shift, assigned_employees: (shift.assigned_employees || []).filter((e: AssignedEmployee) => e.id !== employeeId),
      }));
      const [firstName, ...lastNameParts] = employeeName.split(' ');
      updateShiftOptimistically(targetShiftId, (shift) => ({
        ...shift, assigned_employees: [...(shift.assigned_employees || []), { id: employeeId, first_name: firstName, last_name: lastNameParts.join(' ') || '', employee_id: employeeId }],
      }));
      refresh().catch((err) => pdebugError('Background refresh failed after employee replan:', err));
    } catch (err: any) { pdebugError('Failed to replan employee:', err); toast.error('Fehler beim Umplanen'); }
  }, [employeeConflictModal, refresh, updateShiftOptimistically]);

  const handleAssignAsset = useCallback(async (shiftId: number, assetId: number) => {
    try { await PlantafelService.assignAssetToShift(shiftId, assetId); toast.success('Asset zugewiesen'); await refresh(); }
    catch (err: any) {
      pdebugError('handleAssignAsset FAILED:', err?.response?.status, err?.response?.data || err?.message);
      if (err?.response?.status === 409) {
        const conflicts = err?.response?.data?.conflicts || [];
        if (conflicts.length > 0) {
          const conflict = conflicts[0];
          toast.error(`Asset-Konflikt: Bereits in "${conflict.crm_task_title || 'anderem Job'}" (${conflict.work_area_team_name || ''}) eingeplant`);
        } else { toast.error(`Asset-Konflikt: ${err?.response?.data?.detail || 'Bereits in anderem Job geplant'}`); }
      } else { toast.error('Fehler beim Zuweisen des Assets'); }
    }
  }, [refresh]);

  const handleAddTeamMember = useCallback(async (teamId: number, employeeId: number, employeeName?: string) => {
    try {
      const currentRoster = await PlantafelService.getWeeklyRoster(teamId, weekStartStr);
      const currentMemberIds = currentRoster.members.map((m: any) => m.employee_id || m.id);
      const currentAssetIds = currentRoster.assets.map((a: any) => a.asset_id || a.id);
      if (currentMemberIds.includes(employeeId)) { toast.error('Mitarbeiter ist bereits im Team'); return; }
      await PlantafelService.setWeeklyRoster(teamId, weekStartStr, [...currentMemberIds, employeeId], currentAssetIds);
      toast.success(`${employeeName || 'Mitarbeiter'} zum Team hinzugefügt (ab KW ${getISOWeek(weekStart)})`);
      await refresh();
    } catch (err: any) { pdebugError('Failed to add team member:', err); toast.error('Fehler beim Hinzufügen'); }
  }, [refresh, weekStartStr, weekStart]);

  const handleRemoveTeamMember = useCallback(async (teamId: number, employeeId: number) => {
    try {
      const currentRoster = await PlantafelService.getWeeklyRoster(teamId, weekStartStr);
      const currentMemberIds = currentRoster.members.map((m) => m.employee_id);
      const currentAssetIds = currentRoster.assets.map((a) => a.asset_id);
      await PlantafelService.setWeeklyRoster(teamId, weekStartStr, currentMemberIds.filter((id) => id !== employeeId), currentAssetIds);
      toast.success(`Mitarbeiter aus Team entfernt (ab KW ${getISOWeek(weekStart)})`);
      await refresh();
    } catch (err: any) { pdebugError('Failed to remove team member:', err); toast.error('Fehler beim Entfernen'); }
  }, [refresh, weekStartStr, weekStart]);

  const handleAddTeamAsset = useCallback(async (teamId: number, assetId: number, assetName?: string) => {
    try {
      const currentRoster = await PlantafelService.getWeeklyRoster(teamId, weekStartStr);
      const currentMemberIds = currentRoster.members.map((m) => m.employee_id);
      const currentAssetIds = currentRoster.assets.map((a) => a.asset_id);
      if (currentAssetIds.includes(assetId)) { toast.error('Asset ist bereits im Team'); return; }
      await PlantafelService.setWeeklyRoster(teamId, weekStartStr, currentMemberIds, [...currentAssetIds, assetId]);
      toast.success(`${assetName || 'Asset'} zum Team hinzugefügt (ab KW ${getISOWeek(weekStart)})`);
      await refresh();
    } catch (err: any) { pdebugError('Failed to add team asset:', err); toast.error('Fehler beim Hinzufügen'); }
  }, [refresh, weekStartStr, weekStart]);

  const handleRemoveTeamAsset = useCallback(async (teamId: number, assetId: number) => {
    try {
      const currentRoster = await PlantafelService.getWeeklyRoster(teamId, weekStartStr);
      const currentMemberIds = currentRoster.members.map((m) => m.employee_id);
      const currentAssetIds = currentRoster.assets.map((a) => a.asset_id);
      await PlantafelService.setWeeklyRoster(teamId, weekStartStr, currentMemberIds, currentAssetIds.filter((id) => id !== assetId));
      toast.success(`Asset aus Team entfernt (ab KW ${getISOWeek(weekStart)})`);
      await refresh();
    } catch (err: any) { pdebugError('Failed to remove team asset:', err); toast.error('Fehler beim Entfernen'); }
  }, [refresh, weekStartStr, weekStart]);

  const taskMap = useMemo(() => {
    const map = new Map<number, CrmTask>();
    backlogTasks.forEach((t) => map.set(t.id, t));
    return map;
  }, [backlogTasks, shifts]);

  const allTeams = useMemo(() => laneGroups.flatMap((g) => g.teams), [laneGroups]);

  const handleDropTask = useCallback(async (taskId: number, dayIndex: number, teamId: number) => {
    pdebug('PlantafelBoard: handleDropTask called:', { taskId, dayIndex, teamId });
    const targetDate = new Date(weekStart);
    targetDate.setDate(targetDate.getDate() + dayIndex);
    const dateStr = formatLocalDate(targetDate);
    const task = taskMap.get(taskId);
    const taskTitle = task?.title || `Task ${taskId}`;
    let teamName = `Team ${teamId}`;
    for (const group of laneGroups) {
      const team = group.teams.find((t) => t.id === teamId);
      if (team) { teamName = team.name; break; }
    }
    pdebug('PlantafelBoard: Scheduling:', { taskId, teamId, dateStr, taskTitle, teamName });
    try { await scheduleBacklogTask(taskId, teamId, dateStr, taskTitle, teamName); }
    catch (err) { pdebugError('scheduleBacklogTask failed:', err); }
  }, [scheduleBacklogTask, weekStart, taskMap, laneGroups]);

  useEffect(() => {
    if (!showProjectsTimeline || days.length === 0) return;
    const lastDay = days[days.length - 1];
    const fromDate = formatLocalDate(weekStart);
    const toDate = lastDay ? lastDay.dateStr : formatLocalDate(weekStart);
    PlantafelService.fetchProjectsTimeline(fromDate, toDate)
      .then((data) => setProjectsTimeline(data))
      .catch((err) => { pdebugError('Failed to load projects timeline:', err); setProjectsTimeline([]); });
  }, [weekStart, days, showProjectsTimeline]);

  useEffect(() => {
    if (!showProjectsTimeline) return;
    PlantafelService.fetchAllProjects()
      .then((data) => setAllProjects(data))
      .catch((err) => { pdebugError('Failed to load all projects:', err); setAllProjects([]); });
  }, [showProjectsTimeline]);

  useEffect(() => {
    const loadAbsences = async () => {
      try { setAbsences(await absenceService.fetchAllAbsences()); }
      catch (err) { pdebugError('Failed to load absences (non-blocking):', err); setAbsences([]); }
    };
    loadAbsences();
  }, [weekStart]);

  const absenceCountsByDate = useMemo(() => {
    const counts: Record<string, { approved: number; requested: number }> = {};
    days.forEach(day => { counts[day.dateStr] = { approved: 0, requested: 0 }; });
    absences.forEach(absence => {
      const startDate = new Date(absence.start_datetime);
      const endDate = new Date(absence.end_datetime);
      days.forEach(day => {
        const dayStart = new Date(day.dateStr + 'T00:00:00');
        const dayEnd = new Date(day.dateStr + 'T23:59:59');
        if (startDate <= dayEnd && endDate >= dayStart) {
          if (absence.status === 'APPROVED') counts[day.dateStr].approved++;
          else if (absence.status === 'REQUESTED') counts[day.dateStr].requested++;
        }
      });
    });
    return counts;
  }, [days, absences]);

  return {
    handleOpenProject,
    projectsTimeline,
    allProjects,
    showProjectsTimeline,
    selectedProjectId,
    setSelectedProjectId,
    createProjectModalOpen,
    setCreateProjectModalOpen,
    absences,
    absModal,
    setAbsModal,
    activeEmployeeId,
    highlightedShiftId,
    teamDropTargetId,
    setTeamDropTargetId,
    employeeConflictModal,
    setEmployeeConflictModal,
    handleHighlightShift,
    handleAbsentEmployeeClick,
    filteredShifts,
    filteredBacklogTasks,
    assignedAssetMap,
    assignedEmployeeMap,
    employeeHoursMap,
    weekStartStr,
    waSimple,
    waComplex,
    teamModalOpen,
    rosterModalOpen,
    selectedWorkArea,
    editingTeam,
    selectedTeamForRoster,
    createJobModal,
    scheduleModal,
    handleScheduleClick,
    closeScheduleModal,
    handleAddTeam,
    handleTeamModalClose,
    handleTeamSaved,
    handleEditRoster,
    handleRosterModalClose,
    handleRosterSaved,
    handleCreateJobClick,
    handleCreateJobModalClose,
    handleJobCreated,
    handleCreateProject,
    handleDayClickFromCalendar,
    handleMonthClickFromYear,
    handleUnscheduleShiftDrop,
    handleProjectCreated,
    handleDropProject,
    handleAssignEmployee,
    handleReplanEmployee,
    handleAssignAsset,
    handleAddTeamMember,
    handleRemoveTeamMember,
    handleAddTeamAsset,
    handleRemoveTeamAsset,
    taskMap,
    allTeams,
    handleDropTask,
    absenceCountsByDate,
  };
}
